import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/Button';
import { ApiError } from '@/lib/api';

/**
 * Falha mostrada ao usuário.
 *
 * Três informações, nessa ordem de importância: o que aconteceu em português,
 * o que fazer agora, e — só depois — o `requestId`, que é o que permite achar
 * a ocorrência no log do servidor sem pedir print de tela.
 */
export function ErrorState({
  error,
  onRetry,
  title,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  const { titulo, detalhe, requestId } = descrever(error, title);

  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-4 rounded-lg border border-destructive/30 bg-destructive/5 px-6 py-10 text-center"
    >
      <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-destructive">{titulo}</p>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">{detalhe}</p>
      </div>
      {onRetry ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={onRetry}
          icon={<RotateCcw className="h-4 w-4" aria-hidden />}
        >
          Tentar de novo
        </Button>
      ) : null}
      {requestId ? (
        <p className="font-mono text-[11px] text-muted-foreground">
          Referência para o suporte: {requestId}
        </p>
      ) : null}
    </div>
  );
}

interface Descricao {
  titulo: string;
  detalhe: string;
  requestId?: string;
}

/** Traduz o erro técnico para o que a pessoa precisa decidir. */
function descrever(error: unknown, titulo?: string): Descricao {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return {
        titulo: titulo ?? 'Sessão expirada',
        detalhe: 'Entre de novo para continuar.',
        ...(error.requestId ? { requestId: error.requestId } : {}),
      };
    }
    if (error.status === 403) {
      return {
        titulo: titulo ?? 'Sem permissão',
        detalhe: 'Seu perfil não alcança esta informação. Fale com quem administra o sistema.',
        ...(error.requestId ? { requestId: error.requestId } : {}),
      };
    }
    if (error.status === 404) {
      return {
        titulo: titulo ?? 'Não encontrado',
        detalhe: 'O registro não existe ou está fora do seu escopo.',
        ...(error.requestId ? { requestId: error.requestId } : {}),
      };
    }
    return {
      titulo: titulo ?? 'A API recusou a requisição',
      detalhe: error.message,
      ...(error.requestId ? { requestId: error.requestId } : {}),
    };
  }

  if (error instanceof Error) {
    return {
      titulo: titulo ?? 'Algo deu errado',
      detalhe: error.message,
    };
  }

  return {
    titulo: titulo ?? 'Algo deu errado',
    detalhe: 'Não foi possível concluir a operação.',
  };
}

interface BoundaryProps {
  children: ReactNode;
  /** Muda de valor a cada navegação, para que a rota seguinte não herde a falha. */
  resetKey?: string;
}

interface BoundaryState {
  error: unknown;
}

/**
 * Última rede de proteção da árvore de renderização.
 *
 * Erro em `render` derruba o React inteiro e deixa a tela branca — sem
 * cabeçalho, sem menu, sem saída. Aqui a casca sobrevive e a pessoa consegue
 * ao menos navegar para outro lugar.
 *
 * Precisa ser classe: não existe equivalente em hook para
 * `componentDidCatch`.
 */
export class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Falha não tratada na interface', error, info.componentStack);
  }

  override componentDidUpdate(anterior: BoundaryProps): void {
    // Sem isto o usuário fica preso na tela de erro: navegar para outra rota
    // trocaria o conteúdo, mas o boundary continuaria em estado de falha.
    if (this.state.error && anterior.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <ErrorState
          error={this.state.error}
          title="Esta tela falhou"
          onRetry={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}
